import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ColumnVisibilityState, useTable } from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import { NetworkPortfolioManagementProject, SortByOption } from "@app/network-portfolio-management/interfaces";
import { createProjectTableColumns, features } from "@app/network-portfolio-management/components/project-table/projectTableColumns";
import { VirtualizedTableHeader } from "@app/shared/virtualized-table/VirtualizedTableHeader";
import { ProjectTableRow } from "@app/network-portfolio-management/components/project-table/ProjectTableRow";
import { VirtualizedTableRowSkeleton } from "@app/shared/virtualized-table/VirtualizedTableRowSkeleton";
import { getColumnGroupBoundaries } from "@app/shared/virtualized-table/getColumnGroupBoundaries";
import { ProjectTableSelectionContext } from "@app/network-portfolio-management/contexts/ProjectTableSelectionContext";
import { TableScrollContainerContext } from "@app/network-portfolio-management/contexts/TableScrollContainerContext";
import { PAGE_SIZE, useProjectInfiniteQuery } from "@app/network-portfolio-management/hooks/useProjectInfiniteQuery";
import { useGapByCountyStatusByPage } from "@app/network-portfolio-management/hooks/useGapByCountyStatusByPage";
import { QESAlert } from "@app/shared/qes-standards/Alert";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { ProjectRunConfigurationType } from "@app/projects/enumerators";

const ROW_HEIGHT = 72;

interface ProjectTableProps {
  columnVisibility: ColumnVisibilityState;
  isSorted?: boolean;
  onColumnVisibilityChange: (visibility: ColumnVisibilityState) => void;
  onLoadedRowsChange?: (rows: NetworkPortfolioManagementProject[]) => void;
  onToggleSelect: (id: number) => void;
  projectIds: number[];
  selectedProjectIds: Set<number>;
  selectionEnabled: boolean;
  /**
   * Forces the loading skeleton to render this many rows regardless of the internal query's
   * state — used by ProjectSection's outer loading gate, which has no real projectIds yet.
   * Rendering with projectIds=[] alone would fall through to "No Project Data to Display" instead
   * of a skeleton, since a disabled query (enabled: totalCount > 0 is false) reports isLoading as
   * false, not true.
   */
  skeletonRowCount?: number;
  sortBy: SortByOption;
  /**
   * Scopes the container/header data-testids — GroupSection mounts one ProjectTable per expanded
   * group, and the default prefix is only unique for the single flat (ungrouped) table.
   */
  testIdPrefix?: string;
}

const DEFAULT_TEST_ID_PREFIX = "network-portfolio-management-project-table";

/**
 * The NPM tabular row view: a frozen-title-column, grouped-header, virtualized table
 * over the same paginated data ProjectInfiniteList consumes. columnPinning is hardcoded to the
 * select + title columns per the AC (not user-configurable). columnVisibility is lifted controlled
 * state, wired end-to-end to ProjectTableColumnPicker via ProjectSection.tsx/GroupSection.tsx.
 * Column reordering is not implemented — no work item currently scopes it.
 */
export const ProjectTable: FC<ProjectTableProps> = ({
  columnVisibility,
  isSorted = false,
  onColumnVisibilityChange,
  onLoadedRowsChange,
  onToggleSelect,
  projectIds,
  selectedProjectIds,
  selectionEnabled,
  skeletonRowCount,
  sortBy,
  testIdPrefix = DEFAULT_TEST_ID_PREFIX,
}) => {
  const totalCount = projectIds.length;
  const scrollRef = useRef<HTMLDivElement>(null);

  const { rows, pages, error, fetchNextPage, hasLoadedAny, hasNextPage, isFetchingNextPage, isLoading } = useProjectInfiniteQuery({
    isSorted,
    projectIds,
    sortBy,
  });

  // isSorted's client-side slicing means pages here already line up with the same page boundaries
  // getProjectsData used, so a page's gap-by-status result always matches the rows it was fetched for.
  const { gapByStatusByProjectId, gapByStatusQueries, pageIndexByProjectId } = useGapByCountyStatusByPage(pages);

  useEffect(() => {
    onLoadedRowsChange?.(rows);
  }, [rows, onLoadedRowsChange]);

  // Keyed by projectId, not row index, so a project's toggle survives leaving and re-entering
  // the virtualizer's overscan window (ProjectTableRow unmounts/remounts there — see
  // useMembershipToggle's MembershipToggleOverride).
  const [membershipByProjectId, setMembershipByProjectId] = useState<Map<number, ProjectRunConfigurationType>>(
    () => new Map(),
  );
  const handleMembershipChange = useCallback((projectId: number, value: ProjectRunConfigurationType) => {
    setMembershipByProjectId((prev) => new Map(prev).set(projectId, value));
  }, []);

  const columns = useMemo(() => createProjectTableColumns({ selectionEnabled }), [selectionEnabled]);

  const table = useTable({
    columns,
    data: rows,
    features,
    getRowId: (row) => String(row.projectId),
    // v9's ColumnPinningState requires both start and end (unlike v8's deep-partial initialState) —
    // end is empty since this table never pins to the end.
    initialState: { columnPinning: { end: [], start: ["actions", "select", "projectTitle"] } },
    onColumnVisibilityChange: (updaterOrValue) => {
      const next = typeof updaterOrValue === "function" ? updaterOrValue(columnVisibility) : updaterOrValue;
      onColumnVisibilityChange(next);
    },
    state: { columnVisibility },
  });

  const tableRows = table.getRowModel().rows;
  const headerRowCount = table.getHeaderGroups().length;
  const columnGroupBoundaries = getColumnGroupBoundaries(table);

  const virtualizer = useVirtualizer({
    count: tableRows.length,
    estimateSize: () => ROW_HEIGHT,
    getScrollElement: () => scrollRef.current,
    overscan: 10,
  });

  const virtualItems = virtualizer.getVirtualItems();
  const lastVirtualIndex = virtualItems.length > 0 ? virtualItems[virtualItems.length - 1].index : -1;

  // Mirrors ProjectInfiniteList's sentinel-based loadMore, but triggered by the virtualizer's
  // rendered range instead of an IntersectionObserver, since rows here aren't all in the DOM.
  useEffect(() => {
    if (lastVirtualIndex >= tableRows.length - 1 && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [lastVirtualIndex, tableRows.length, hasNextPage, isFetchingNextPage, fetchNextPage]);

  const isAbortError = (error as DOMException)?.name === "AbortError";
  const initialLoadError = error && !isAbortError && !hasLoadedAny ? "Failed to load Project. Please try again." : null;
  const scrollLoadError = error && !isAbortError && hasLoadedAny ? "Failed to load Additional Projects. Please try again." : null;

  if (isLoading || skeletonRowCount != null) {
    const count = skeletonRowCount ?? Math.min(PAGE_SIZE, totalCount);
    return (
      <div className="virtualized-table" data-testid={testIdPrefix}>
        <div
          className="virtualized-table__scroll"
          role="table"
          aria-label="Network Projects"
          aria-busy="true"
          aria-rowcount={skeletonRowCount != null ? -1 : headerRowCount + totalCount}
          aria-colcount={table.getVisibleLeafColumns().length}
        >
          <VirtualizedTableHeader columnGroupBoundaries={columnGroupBoundaries} table={table} testIdPrefix={testIdPrefix} />
          <div className="virtualized-table__body" role="rowgroup" style={{ minWidth: table.getTotalSize() }}>
            {Array.from({ length: count }, (_, i) => (
              <VirtualizedTableRowSkeleton
                key={i}
                columnGroupBoundaries={columnGroupBoundaries}
                rowHeight={ROW_HEIGHT}
                table={table}
              />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (initialLoadError) {
    return (
      <QESAlert type="danger" persist={true}>
        {initialLoadError}
      </QESAlert>
    );
  }

  if (rows.length === 0) {
    return <h3 className="text-muted no-content-text">No Project Data to Display</h3>;
  }

  return (
    <ProjectTableSelectionContext.Provider value={{ onToggleSelect, selectedProjectIds }}>
      <TableScrollContainerContext.Provider value={scrollRef}>
        <div className="virtualized-table" data-testid={testIdPrefix}>
          <div
            className="virtualized-table__scroll"
            ref={scrollRef}
            role="table"
            aria-label="Network Projects"
            aria-rowcount={headerRowCount + totalCount}
            aria-colcount={table.getVisibleLeafColumns().length}
          >
            <VirtualizedTableHeader columnGroupBoundaries={columnGroupBoundaries} table={table} testIdPrefix={testIdPrefix} />
            <div
              className="virtualized-table__body"
              role="rowgroup"
              style={{ height: virtualizer.getTotalSize(), minWidth: table.getTotalSize() }}
            >
              {virtualItems
                .filter((virtualRow) => virtualRow.index < tableRows.length)
                .map((virtualRow) => {
                  const project = tableRows[virtualRow.index].original;
                  const pageIndex = pageIndexByProjectId.get(project.projectId);
                  const pageQuery = pageIndex != null ? gapByStatusQueries[pageIndex] : undefined;
                  return (
                    <ProjectTableRow
                      key={tableRows[virtualRow.index].id}
                      ariaRowIndex={headerRowCount + virtualRow.index + 1}
                      columnGroupBoundaries={columnGroupBoundaries}
                      gapByCountyStatus={gapByStatusByProjectId.get(project.projectId)}
                      gapByCountyStatusHasError={!!pageQuery?.error}
                      isGapByCountyStatusLoading={!!pageQuery?.isLoading}
                      membership={
                        membershipByProjectId.get(project.projectId) ??
                        ProjectRunConfigurationType.DualMembershipPrimary
                      }
                      onMembershipChange={handleMembershipChange}
                      row={tableRows[virtualRow.index]}
                      virtualRow={virtualRow}
                    />
                  );
                })}
            </div>
          </div>
          {scrollLoadError && (
            <QESAlert type="warning" persist={true}>
              {scrollLoadError}
            </QESAlert>
          )}
          {isFetchingNextPage && !scrollLoadError && (
            <div className="network-portfolio-management-project-table__loading-row">
              <QesIcon iconName="Spinner" isDecorative className="qes-icon-spin" />
              Loading more projects…
            </div>
          )}
          {!hasNextPage && !isFetchingNextPage && (
            <p className="network-portfolio-management-project-table__end-of-list" data-testid="network-portfolio-management-end-of-list">
              All Projects Loaded
            </p>
          )}
        </div>
      </TableScrollContainerContext.Provider>
    </ProjectTableSelectionContext.Provider>
  );
};
